/**
 * @features/dashboard/config/navigationConfig.js
 * ----------------------------------------------
 * Declarative role → navigation items. ONE source of truth for the
 * dashboard sidebar/tab structure of every persona.
 *
 * Each item is:
 *   {
 *     id:       "leads",                  // unique within the role
 *     label:    "Leads",
 *     icon:     LucideIcon component,
 *     to:       "/team/leads",             // react-router target
 *     perm:     "lead:view",               // optional gate (hidden if can()=false)
 *     badge?:   () => number | string      // optional dynamic badge
 *     children: [ ...nested items ]        // optional
 *   }
 *
 * Why a config file and not JSX-coded?
 *   • Adding a tab is now a single object literal, not a CSS / layout edit.
 *   • The same config powers desktop sidebar, mobile bottom-nav extras,
 *     and any future command-palette / breadcrumb generator.
 *
 * v2.71.27 — Initial.
 * v2.71.33 — Feature parity restoration. Every legacy admin tab + every
 *            dealer/builder tab + every team tab now has a sidebar entry
 *            pointing at a working route. No orphan routes.
 * v2.71.39 — Centralized persona-aware navigation system. This file is
 *            now the SINGLE source of truth for:
 *              • sidebar items (NAV_CONFIG)
 *              • mobile bottom-nav 5-slot tabs (BOTTOM_NAV_CONFIG)
 *              • per-page mobile quick-actions (QUICK_ACTIONS_CONFIG)
 *              • role → persona resolution (re-exported from permissions)
 *              • persona "home" route + display label (PERSONA_META)
 *            Adding a navigation item anywhere is now a config edit, never
 *            a component edit.
 */

import {
  Home, Building2, Heart, ClipboardList, Bell, User, Users, MessageCircle,
  Layers, BarChart3, Tag, Crown, FileText, MapPin, BadgeCheck, Wallet,
  ScrollText, Star, ShieldCheck, Settings as SettingsIcon, UserPlus, Search,
  Plus, LayoutDashboard, Shield, Map as MapIcon, Sparkles, Navigation,
  HardDrive,
} from "lucide-react";

/** All currently-known sidebars, keyed by persona. */
export const NAV_CONFIG = {
  buyer: [
    { id: "overview",       label: "Overview",         icon: Home,         to: "/account" },
    { id: "saved",          label: "Saved",            icon: Heart,        to: "/saved",            perm: "property:save" },
    { id: "recently",       label: "Recently Viewed",  icon: ClipboardList, to: "/account/recent" },
    { id: "enquiries",      label: "My Enquiries",     icon: MessageCircle, to: "/account/enquiries", perm: "enquiry:create" },
    { id: "alerts",         label: "Property Alerts",  icon: Bell,         to: "/account/alerts" },
    { id: "profile",        label: "Profile",          icon: User,         to: "/profile" },
    { id: "plans",          label: "Plans",            icon: Crown,        to: "/pricing",          perm: "plan:purchase" },
  ],
  owner: [
    // Owner inherits buyer; additional items added at the top of the sidebar.
    { id: "my-listings",    label: "My Listings",      icon: Building2,    to: "/account/listings", perm: "property:edit-own" },
    { id: "add-property",   label: "Add Property",     icon: Tag,          to: "/account/add",      perm: "property:create" },
  ],
  dealer: [
    { id: "overview",       label: "Overview",         icon: Home,         to: "/dealer/overview" },
    { id: "listings",       label: "My Listings",      icon: Building2,    to: "/dealer/listings",  perm: "property:edit-own" },
    { id: "add",            label: "Add Property",     icon: Tag,          to: "/dealer/submit",    perm: "property:create" },
    { id: "leads",          label: "Leads",            icon: Users,        to: "/dealer/leads",     perm: "lead:view-own" },
    { id: "earnings",       label: "Earnings",         icon: Wallet,       to: "/dealer/earnings" },
    { id: "shortlists",     label: "Shortlists",       icon: Layers,       to: "/dealer/shortlists", perm: "shortlist:view" },
    { id: "saved",          label: "Saved",            icon: Heart,        to: "/dealer/saved",     perm: "property:save" },
    { id: "projects",       label: "Projects",         icon: Building2,    to: "/dealer/projects" },
    { id: "explore",        label: "Marketplace",      icon: Search,       to: "/dealer/explore" },
    { id: "analytics",      label: "Analytics",        icon: BarChart3,    to: "/dealer/analytics" },
    { id: "notifications",  label: "Notifications",    icon: Bell,         to: "/dealer/notifications" },
    { id: "kyc",            label: "KYC",              icon: BadgeCheck,   to: "/dealer/kyc" },
    { id: "plans",          label: "Subscription",     icon: Crown,        to: "/dealer/plan",      perm: "plan:purchase" },
    { id: "profile",        label: "Profile",          icon: User,         to: "/dealer/profile" },
  ],
  builder: [
    // v2.71.65 — Builder console redesign. Inventory-control-panel focused.
    // Renamed Explore→Marketplace Insights (restricted), Inquiries→Leads,
    // Performance→Analytics. New "My Listings" surface for standalone
    // units (separate from projects). Brochures/Updates/Visits moved to
    // secondary surfaces (still routable, hidden from primary nav).
    // Customer contact is NEVER surfaced — HouseEntry routes via Call team.
    { id: "overview",              label: "Overview",               icon: Home,         to: "/builder/overview" },
    { id: "projects",              label: "My Projects",            icon: Building2,    to: "/builder/projects",         perm: "project:edit-own" },
    { id: "listings",              label: "My Listings",            icon: Layers,       to: "/builder/listings" },
    { id: "add",                   label: "Add",                    icon: Tag,          to: "/builder/add",              perm: "project:create" },
    { id: "leads",                 label: "Leads",                  icon: MessageCircle, to: "/builder/inquiries" },
    { id: "analytics",             label: "Analytics",              icon: BarChart3,    to: "/builder/analytics" },
    { id: "marketplace-insights",  label: "Marketplace Insights",   icon: Search,       to: "/builder/marketplace" },
    { id: "notifications",         label: "Notifications",          icon: Bell,         to: "/builder/notifications" },
    { id: "kyc",                   label: "KYC",                    icon: BadgeCheck,   to: "/dealer/kyc" },
    { id: "plans",                 label: "Subscription",           icon: Crown,        to: "/dealer/plan",              perm: "plan:purchase" },
    { id: "profile",               label: "Profile",                icon: User,         to: "/builder/profile" },
  ],
  caller: [
    // v2.71.50 — Caller workflow-first. Removed redundant "Dashboard"
    // entry (was /team — which now redirects to /team/workflow anyway).
    { id: "workflow",       label: "Lead Workflow",    icon: Sparkles,     to: "/team/workflow",    perm: "lead:view" },
    { id: "leads",          label: "My Leads",         icon: Users,        to: "/team/leads",       perm: "lead:view" },
    { id: "properties",     label: "Browse Properties", icon: Building2,   to: "/team/properties" },
    { id: "shortlists",     label: "Shortlists",       icon: Layers,       to: "/team/shortlists",  perm: "shortlist:view" },
    { id: "callbacks",      label: "Callbacks",        icon: Bell,         to: "/team/callbacks",   perm: "lead:respond" },
    { id: "map",            label: "Map View",         icon: MapPin,       to: "/team/map" },
  ],
  field_agent: [
    // v2.71.50 — Self-contained (no inheritance from caller).
    // Field-agent workflow is visits-first; lead workflow is secondary,
    // shortlists tertiary. No "Dashboard" wrapper anymore — /team auto-
    // redirects to /team/field via TeamHome.
    { id: "field",          label: "Today's Visits",   icon: Navigation,   to: "/team/field",       perm: "field:visit-log" },
    { id: "workflow",       label: "Lead Workflow",    icon: Sparkles,     to: "/team/workflow",    perm: "lead:view" },
    { id: "properties",     label: "Browse Properties", icon: Building2,   to: "/team/properties" },
    { id: "map",            label: "Map View",         icon: MapPin,       to: "/team/map" },
    { id: "shortlists",     label: "Shortlists",       icon: Layers,       to: "/team/shortlists",  perm: "shortlist:view" },
  ],
  admin: [
    { id: "overview",       label: "Overview",         icon: Home,         to: "/admin/overview" },
    { id: "listings",       label: "Listings",         icon: Building2,    to: "/admin/listings" },
    { id: "approvals",      label: "Approvals",        icon: ScrollText,   to: "/admin/approvals" },
    { id: "crm",            label: "Smart CRM",        icon: Star,         to: "/admin/crm" },
    { id: "users",          label: "Users",            icon: Users,        to: "/admin/users" },
    { id: "dealers",        label: "Dealers / KYC",    icon: BadgeCheck,   to: "/admin/dealers" },
    { id: "add-client",     label: "Add Client",       icon: UserPlus,     to: "/admin/add-client" },
    { id: "plans",          label: "Plans",            icon: Crown,        to: "/admin/plans" },
    { id: "transactions",   label: "Transactions",     icon: Wallet,       to: "/admin/transactions" },
    { id: "projects",       label: "Projects",         icon: Building2,    to: "/admin/projects" },
    { id: "shortlists",     label: "Shortlists",       icon: Layers,       to: "/admin/shortlists" },
    { id: "content",        label: "Content / CMS",    icon: FileText,     to: "/admin/content" },
    { id: "pages",          label: "Pages",            icon: FileText,     to: "/admin/pages" },
    { id: "filters",        label: "Filters & Areas",  icon: Tag,          to: "/admin/filters" },
    { id: "locations",      label: "Locations",        icon: MapPin,       to: "/admin/locations" },
    { id: "testimonials",   label: "Testimonials",     icon: Star,         to: "/admin/testimonials" },
    { id: "team",           label: "Team",             icon: ShieldCheck,  to: "/admin/team" },
    { id: "settings",       label: "Settings",         icon: SettingsIcon, to: "/admin/settings" },
    { id: "backup",         label: "Backup Center",    icon: HardDrive,    to: "/admin/backup" },
    { id: "notifications",  label: "Notifications",    icon: Bell,         to: "/admin/notifications" },
    { id: "analytics",      label: "Analytics",        icon: BarChart3,    to: "/admin/crm" },
  ],
};

/** Personas that inherit nav items from a parent persona (prepended). */
const NAV_INHERITS = {
  owner: "buyer",
  // v2.71.50 — field_agent is now self-contained per the workflow
  // separation requirement (visits-first surface, not caller surface).
};

/** Resolve the full navigation list for a persona, applying inheritance. */
export function navForPersona(persona) {
  const own = NAV_CONFIG[persona] || [];
  const parentKey = NAV_INHERITS[persona];
  if (!parentKey) return own;
  // Child items appear first, then parent items the child didn't override.
  const parent = NAV_CONFIG[parentKey] || [];
  const ownIds = new Set(own.map((i) => i.id));
  return [...own, ...parent.filter((i) => !ownIds.has(i.id))];
}

/* ───────────────────────────────────────────────────────────────────── */
/*  Persona metadata                                                      */
/* ───────────────────────────────────────────────────────────────────── */

/**
 * Display label + canonical "home" landing route per persona. Used by
 * the MobileMoreSheet header, breadcrumbs, default redirects, etc.
 */
export const PERSONA_META = {
  anon:        { label: "Account",       home: "/" },
  buyer:       { label: "My Account",    home: "/account" },
  owner:       { label: "My Account",    home: "/account" },
  dealer:      { label: "Dealer",        home: "/dealer/overview" },
  builder:     { label: "Builder",       home: "/builder/overview" },
  caller:      { label: "Team",          home: "/team/workflow" },
  field_agent: { label: "Field Agent",   home: "/team/field" },
  admin:       { label: "Admin",         home: "/admin/overview" },
};

/* ───────────────────────────────────────────────────────────────────── */
/*  Mobile bottom navigation                                              */
/* ───────────────────────────────────────────────────────────────────── */

/**
 * 5-slot mobile bottom-nav per persona. The middle slot is rendered as a
 * raised primary FAB; slots are ordered left-to-right.
 *
 * Item shape:
 *   {
 *     id:       "listings",          // unique within the persona
 *     label:    "Listings",          // short — fits under icon (8-10 chars max)
 *     icon:     LucideIcon,
 *     to:       "/admin/listings",   // canonical route URL
 *     match?:   "/admin/listings",   // prefix used for active-state detection
 *     primary?: true,                // raised FAB-style center slot
 *     perm?:    "property:create",   // optional gate
 *     testid?:  "mb-admin-listings", // stable test selector
 *   }
 *
 * Backward compatibility: legacy `?tab=` deep-links continue to work
 * through AdminEntry / DealerEntry / TeamEntry redirect maps.
 */
export const BOTTOM_NAV_CONFIG = {
  anon: [
    { id: "home",       label: "Home",     icon: Home,            to: "/",                  testid: "mb-home" },
    { id: "search",     label: "Search",   icon: Search,          to: "/?focus=search",     testid: "mb-search" },
    { id: "map",        label: "Map",      icon: MapIcon,         to: "/map",               testid: "mb-map" },
    { id: "projects",   label: "Projects", icon: Building2,       to: "/projects",          testid: "mb-projects" },
    { id: "signin",     label: "Sign in",  icon: User,            to: "/access",            testid: "mb-signin" },
  ],
  buyer: [
    { id: "home",       label: "Home",     icon: Home,            to: "/",                  testid: "mb-home" },
    { id: "search",     label: "Search",   icon: Search,          to: "/?focus=search",     testid: "mb-search" },
    { id: "map",        label: "Map",      icon: MapIcon,         to: "/map",               testid: "mb-map" },
    { id: "saved",      label: "Saved",    icon: Heart,           to: "/saved",             testid: "mb-saved" },
    { id: "profile",    label: "Profile",  icon: User,            to: "/profile",           testid: "mb-profile" },
  ],
  // Owner inherits buyer (same 5 slots until owner-specific UX lands).
  dealer: [
    { id: "home",       label: "Home",     icon: Home,            to: "/",                  testid: "mb-home" },
    { id: "listings",   label: "My",       icon: LayoutDashboard, to: "/dealer/listings",   match: "/dealer/listings", testid: "mb-dealer-mine" },
    { id: "add",        label: "Add",      icon: Plus,            to: "/dealer/submit",     primary: true, testid: "mb-dealer-add" },
    { id: "leads",      label: "Leads",    icon: MessageCircle,   to: "/dealer/leads",      match: "/dealer/leads", testid: "mb-dealer-leads" },
    { id: "profile",    label: "Profile",  icon: User,            to: "/profile",           testid: "mb-dealer-profile" },
  ],
  builder: [
    { id: "home",       label: "Home",     icon: Home,            to: "/builder/overview",  match: "/builder/overview", testid: "mb-home" },
    { id: "projects",   label: "Projects", icon: Building2,       to: "/builder/projects",  match: "/builder/projects", testid: "mb-builder-projects" },
    { id: "add",        label: "Add",      icon: Plus,            to: "/dealer/submit",     primary: true, testid: "mb-builder-add" },
    { id: "inquiries",  label: "Inquiries", icon: MessageCircle,  to: "/builder/inquiries", match: "/builder/inquiries", testid: "mb-builder-leads" },
    { id: "profile",    label: "Profile",  icon: User,            to: "/profile",           testid: "mb-builder-profile" },
  ],
  caller: [
    { id: "home",       label: "Workflow", icon: Sparkles,        to: "/team/workflow",     match: "/team/workflow", testid: "mb-home" },
    { id: "leads",      label: "Leads",    icon: MessageCircle,   to: "/team/leads",        match: "/team/leads", testid: "mb-team-leads" },
    { id: "properties", label: "Browse",   icon: Search,          to: "/team/properties",   match: "/team/properties", primary: true, testid: "mb-team-properties" },
    { id: "shortlists", label: "Sent",     icon: Layers,          to: "/team/shortlists",   match: "/team/shortlists", testid: "mb-team-shortlists" },
    { id: "profile",    label: "Profile",  icon: User,            to: "/profile",           testid: "mb-team-profile" },
  ],
  field_agent: [
    { id: "home",       label: "Visits",   icon: Navigation,      to: "/team/field",        match: "/team/field", testid: "mb-home" },
    { id: "workflow",   label: "Leads",    icon: Sparkles,        to: "/team/workflow",     match: "/team/workflow", testid: "mb-field-workflow" },
    { id: "properties", label: "Browse",   icon: Search,          to: "/team/properties",   match: "/team/properties", primary: true, testid: "mb-field-properties" },
    { id: "map",        label: "Map",      icon: MapIcon,         to: "/team/map",          match: "/team/map", testid: "mb-field-map" },
    { id: "profile",    label: "Profile",  icon: User,            to: "/profile",           testid: "mb-field-profile" },
  ],
  admin: [
    { id: "home",       label: "Home",     icon: Home,            to: "/",                  testid: "mb-home" },
    { id: "listings",   label: "Listings", icon: LayoutDashboard, to: "/admin/listings",    match: "/admin/listings", testid: "mb-admin-listings" },
    // v2.71.37 — `?add=1` auto-opens the Add-Property dialog (handled by
    // AdminPage's useEffect). Replaces the legacy "create=1" payload that
    // was lost during redirect.
    { id: "add",        label: "Add",      icon: Plus,            to: "/admin/listings?add=1", primary: true, testid: "mb-admin-add" },
    { id: "leads",      label: "Leads",    icon: MessageCircle,   to: "/admin/crm",         match: "/admin/crm", testid: "mb-admin-leads" },
    { id: "admin",      label: "Admin",    icon: Shield,          to: "/admin/users",       match: "/admin/users", testid: "mb-admin-panel" },
  ],
};

/** Personas that inherit bottom-nav from a parent persona. */
const BOTTOM_NAV_INHERITS = {
  owner: "buyer",
};

/** Resolve the bottom nav for a persona, applying inheritance. */
export function bottomNavForPersona(persona) {
  const own = BOTTOM_NAV_CONFIG[persona];
  if (own && own.length) return own;
  const parentKey = BOTTOM_NAV_INHERITS[persona];
  if (parentKey) return BOTTOM_NAV_CONFIG[parentKey] || [];
  return BOTTOM_NAV_CONFIG.anon;
}

/* ───────────────────────────────────────────────────────────────────── */
/*  Mobile quick actions (opt-in per page)                                */
/* ───────────────────────────────────────────────────────────────────── */

/**
 * Per-page mobile pill row of high-frequency CTAs. Keyed by **route key**
 * (the canonical pathname) so the same config can serve any persona that
 * lands on that route. Each entry is consumed by `<MobileQuickActions>`
 * via the `quickActions` prop on `<DashboardLayout>`.
 *
 * Item shape mirrors `BOTTOM_NAV_CONFIG`:
 *   { id, label, icon, to|onClick, primary?, testid? }
 *
 * Adding a per-page row is now a config edit:
 *   QUICK_ACTIONS_CONFIG["/admin/listings"] = [...]
 *
 * Empty/undefined = no quick-action row rendered.
 */
export const QUICK_ACTIONS_CONFIG = {
  "/admin/overview": [
    { id: "approvals",  label: "Approvals",    icon: ScrollText, to: "/admin/approvals",      testid: "mqa-approvals" },
    { id: "add",        label: "Add Property", icon: Plus,       to: "/admin/listings?add=1", primary: true, testid: "mqa-add-property" },
    { id: "add-client", label: "Add Client",   icon: UserPlus,   to: "/admin/add-client",     testid: "mqa-add-client" },
    { id: "plans",      label: "Plans",        icon: Crown,      to: "/admin/plans",          testid: "mqa-plans" },
    { id: "txns",       label: "Transactions", icon: Wallet,     to: "/admin/transactions",   testid: "mqa-transactions" },
  ],
  "/dealer/overview": [
    { id: "add",        label: "Add Property", icon: Plus,       to: "/dealer/submit",        primary: true, testid: "mqa-dealer-add" },
    { id: "listings",   label: "My Listings",  icon: Building2,  to: "/dealer/listings",      testid: "mqa-dealer-listings" },
    { id: "leads",      label: "Leads",        icon: Users,      to: "/dealer/leads",         testid: "mqa-dealer-leads" },
    { id: "plan",       label: "Plan",         icon: Crown,      to: "/dealer/plan",          testid: "mqa-dealer-plan" },
  ],
  "/builder/overview": [
    { id: "add-project", label: "New Project",     icon: Plus,         to: "/dealer/submit",        primary: true, testid: "mqa-builder-new-project" },
    { id: "projects",    label: "My Projects",     icon: Building2,    to: "/builder/projects",     testid: "mqa-builder-projects" },
    { id: "inquiries",   label: "Inquiries",       icon: MessageCircle, to: "/builder/inquiries",   testid: "mqa-builder-inquiries" },
    { id: "visits",      label: "Site Visits",     icon: MapPin,       to: "/builder/visits",       testid: "mqa-builder-visits" },
    { id: "analytics",   label: "Performance",     icon: BarChart3,    to: "/builder/analytics",    testid: "mqa-builder-analytics" },
  ],
  "/account": [
    { id: "saved",      label: "Saved",        icon: Heart,         to: "/saved",             testid: "mqa-buyer-saved" },
    { id: "enquiries",  label: "My Enquiries", icon: MessageCircle, to: "/account/enquiries", testid: "mqa-buyer-enquiries" },
    { id: "recent",     label: "Recent",       icon: ClipboardList, to: "/account/recent",    testid: "mqa-buyer-recent" },
    { id: "plans",      label: "Plans",        icon: Crown,         to: "/pricing",           testid: "mqa-buyer-plans" },
  ],
  "/team/leads": [
    { id: "properties", label: "Browse",       icon: Search,    to: "/team/properties",       testid: "mqa-team-properties" },
    { id: "map",        label: "Map",          icon: MapIcon,   to: "/team/map",              testid: "mqa-team-map" },
    { id: "shortlists", label: "Shortlists",   icon: Layers,    to: "/team/shortlists",       testid: "mqa-team-shortlists" },
  ],
};

/** Resolve the quick-action row for the current persona + route. */
export function quickActionsFor(persona, routeKey) {
  // Persona-scoped override could be added here in the future:
  //   QUICK_ACTIONS_CONFIG[`${persona}:${routeKey}`] ?? QUICK_ACTIONS_CONFIG[routeKey]
  void persona;
  return QUICK_ACTIONS_CONFIG[routeKey] || null;
}

